import { motion } from "motion/react";
import { ComponentType, ReactNode } from "react";
import { TbShieldCheck, TbUsers } from "react-icons/tb";

import { ClinicArrivalTasksSection } from "@/pages/HotelPlanning/clinic-arrival/sections/ClinicArrivalTasksSection";
import { ClinicArrivalUnassignedSection } from "@/pages/HotelPlanning/clinic-arrival/sections/ClinicArrivalUnassignedSection";
import { HotelClinicListItem, HotelClinicTaskResolved } from "@/pages/HotelPlanning/clinic-arrival/types";
import { HotelClinicManagerTasksByDateResponseSchemaType } from "@/schemas/hotelClinicSchema";
import { CommonStatusItem } from "@/types/global";

import { softColors } from "./colors";

export type HotelClinicArrivalSectionLayout = "unassigned" | "tasks";

export type HotelClinicArrivalSectionDefinition = CommonStatusItem & {
    wrapperClassName: string;
    delay: number;
};

export type HotelClinicArrivalCommonProps = {
    data: HotelClinicManagerTasksByDateResponseSchemaType;
    loading?: boolean;
    reloadTable: () => void;
} & CommonStatusItem;

export type HotelClinicArrivalTableProps<T extends HotelClinicTaskResolved | HotelClinicListItem> = Omit<
    HotelClinicArrivalCommonProps,
    "data" | "reloadTable"
> & {
    allRows: T[];
    rows: T[];
};

export type HotelClinicArrivalSectionItem = HotelClinicArrivalSectionDefinition & {
    Section: ComponentType<HotelClinicArrivalCommonProps>;
};

export type HotelClinicArrivalSectionsList = HotelClinicArrivalSectionItem[];

export function HotelClinicArrivalSectionTitle({ label }: { label: string }) {
    return <div className="font-semibold text-slate-700 flex items-center gap-1">{label}</div>;
}

/** Layout shell: title + tooltip + viewport wrapper around section content (tables). */
export function HotelClinicArrivalSectionFrame({
    children,
    delay,
    wrapperClassName,
}: {
    children: ReactNode;
} & Pick<HotelClinicArrivalSectionDefinition, "delay" | "wrapperClassName">) {
    return (
        <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay }}
            className={wrapperClassName}
        >
            {children}
        </motion.section>
    );
}

export const hotelClinicArrivalSections: HotelClinicArrivalSectionsList = [
    {
        component: () => TbUsers,
        ...softColors.green,
        tooltip: {
            title: "Atanmamış Yolcular",
            description: "Atanmamış yolcular, henüz bir göreve atanmamış yolculardır.",
        },
        delay: 0,
        Section: ClinicArrivalUnassignedSection,
        wrapperClassName: "flex w-full shrink-0 flex-col gap-2 min-h-0 min-w-0",
    },
    {
        component: () => TbShieldCheck,
        ...softColors.indigo,
        tooltip: {
            title: "Görevler",
            description: "Görevler, yolcuların kliniğe geliş tarihleri ve görevleri için kullanılan alanlardır.",
        },
        delay: 0.1,
        Section: ClinicArrivalTasksSection,
        wrapperClassName: "flex min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-hidden",
    },
];
